import styles from './login-page.module.css';
import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { Banner } from '@/components/banner';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, Navigate, useLocation } from '@/util/react-router-dom';
import { useLogin } from '@/api/login';
import { useCurrentUser } from '@/contexts/current-user-context';
import { z } from 'zod';
import { ExternalLoginOption, LoginErrorResponse, LoginErrorType } from '@/api/api.schemas';
import { Form } from '@/components/form';
import { requiredString, zt } from '@/util/validators';
import { UserEntryLayout, UserEntryLayoutBanner } from '@/components/auth/user-entry-layout';
import { useTranslation } from '@/contexts/i18n-context';
import { Section } from '@/components/containers/section';
import { useBranding } from '@/outlets/branding-outlet';
import { MarketingSpot } from '@/components/marketing-spot';
import clsx from 'clsx';
import { ResetPasswordLink } from '@/components/auth/reset-password-link';

const loginSchema = z.object({
  username: requiredString('login.username.error'),
  password: z.string().min(1, { message: zt('login.password.error') }),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const hasSsoLoginError = !!location.state && 'ssoError' in location.state;

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  const navigate = useNavigate();
  const { mutate: login, isPending, error } = useLogin();
  const { setUserData } = useCurrentUser();

  const getErrorMessage = (error: LoginErrorResponse) => {
    const lowRemainingAttempts =
      error.remainingAttempts != null &&
      (error.remainingAttempts === 1 || error.remainingAttempts === 2);

    switch (error.type) {
      case LoginErrorType.InvalidCredentials:
        return lowRemainingAttempts
          ? t('login.errors.lockWarning', {
              resetPasswordLink: <ResetPasswordLink key="reset-password-link" />,
            })
          : t('login.errors.invalidCredentials');
      case LoginErrorType.AccountLocked:
        return t('login.errors.accountLocked', {
          resetPasswordLink: <ResetPasswordLink key="reset-password-link" />,
        });
      case LoginErrorType.InvalidUsername:
        return t('login.errors.invalidUsername', {
          support: (
            <Button variant="link" as="a" to="/support" key="contact-support-link">
              {t(`login.support`)}
            </Button>
          ),
        });
      default:
        return t('common.errors.unknown');
    }
  };

  const { createAccountEnabled, loginPageDisabled, logoutText, logoutUrl, externalLoginOptions } =
    useBranding();

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    login(
      { data },
      {
        onSuccess: (result) => {
          setUserData(result.token);
          navigate('/verify-identity', {
            state: {
              method: result.twoFactorMethod,
              target: result.target,
              availableTfaMethods: result.availableTfaMethods,
            },
          });
        },
        onError: (error) => {
          if (error.type === LoginErrorType.MissingEmail) {
            navigate('/missing-email');
          }
        },
      },
    );
  };

  if (loginPageDisabled && createAccountEnabled) {
    return <Navigate to="/register" replace />;
  }

  return (
    <>
      <UserEntryLayout
        titleContent={<MarketingSpot page="login" spot="top" />}
        titleClassName={styles.title}
      >
        {loginPageDisabled ? (
          <Section isFormSection={false}>
            {hasSsoLoginError && (
              <Banner type="error" className={styles.ssoErrorBannerLoginDisabled}>
                {t('login.errors.sso', {
                  support: (
                    <Button variant="link" as="a" to="/support" key="contact-support-link">
                      {t('login.support')}
                    </Button>
                  ),
                })}
              </Banner>
            )}
            {externalLoginOptions.length ? (
              <>
                <h2 className="h4">{t('login.loginToYourAccount')}</h2>
                <ExternalLoginOptions
                  options={externalLoginOptions}
                  className={styles.canNotLoginButton}
                />
              </>
            ) : (
              <>
                <h2 className="h4">{t('login.canNotLogin.title')}</h2>
                <Button as="a" to={logoutUrl ?? '/'} className={styles.canNotLoginButton}>
                  {logoutText}
                </Button>
              </>
            )}
          </Section>
        ) : (
          <Section isFormSection={false}>
            <h2 className="h4">{t('login.formTitle')}</h2>
            <Form className={styles.form} form={form} onSubmit={onSubmit}>
              {hasSsoLoginError && (
                <Banner type="error">
                  {t('login.errors.sso', {
                    support: (
                      <Button variant="link" as="a" to="/support" key="contact-support-link">
                        {t('login.support')}
                      </Button>
                    ),
                  })}
                </Banner>
              )}
              {!!error && <Banner type="error">{getErrorMessage(error)}</Banner>}
              <TextInput
                label={t('login.username.label')}
                placeholder={t('login.username.placeholder')}
                name="username"
                withIconsIndicators={false}
              />
              <TextInput
                label={t('login.password.label')}
                type="password"
                placeholder={t('login.password.placeholder')}
                name="password"
                withIconsIndicators={false}
              />
              <Button type="submit" variant="filled" color="accent" loading={isPending}>
                {t('common.logIn')}
              </Button>
              {!!externalLoginOptions.length && (
                <>
                  <p className={clsx('bold', styles.or)}>{t('common.or')}</p>
                  <ExternalLoginOptions options={externalLoginOptions} />
                </>
              )}
              <span className={styles.forgotUsernameOrPassword}>
                {t('login.forgotUsernameOrPassword', {
                  username: (
                    <Link key="forgot-username" to="/restore-usernames">
                      {t('login.usernameLink')}
                    </Link>
                  ),
                  password: (
                    <Link key="forgot-password" to="/forgot-password">
                      {t('login.passwordLink')}
                    </Link>
                  ),
                })}
              </span>
            </Form>
          </Section>
        )}
        {createAccountEnabled && (
          <p className={styles.registerLink}>
            {t('login.registerLink')}
            <Button variant="link" color="accent" key="register-link" as="a" to="/register">
              {t('login.createAccount')}
            </Button>
          </p>
        )}
        <MarketingSpot page="login" spot="bottom" />
      </UserEntryLayout>
      <UserEntryLayoutBanner />
    </>
  );
}

interface ExternalLoginOptionsProps {
  options: ExternalLoginOption[];
  className?: string;
}

function ExternalLoginOptions({ options, className }: ExternalLoginOptionsProps) {
  if (!options.length) {
    return null;
  }

  return (
    <div className={clsx(styles.externalLoginOptions, className)}>
      {options.map((option) => (
        <Button
          key={option.url}
          as="a"
          color="accent"
          className={styles.externalLoginOptionLabel}
          to={option.url}
        >
          {option.logoUrl && <img src={option.logoUrl} alt="" aria-hidden />}
          {option.text}
        </Button>
      ))}
    </div>
  );
}
